import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { environment } from '@env/environment';
import { DateTimezoneService } from './date-timezone.service';

@Injectable({
  providedIn: 'root',
})
export class GoogleAnalyticsReportService {
  private onReady$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private isReadyInitialized: boolean = false;
  private authorizationReference: HTMLElement;

  public get gapi(): any {
    return (window as any).gapi;
  }

  constructor(private dateTimezoneService: DateTimezoneService) {
    this.checkReady();
  }

  public get onReady(): BehaviorSubject<boolean> {
    if (!this.isReadyInitialized) {
      this.checkReady();
    }
    return this.onReady$;
  }

  public authorize(container: HTMLElement) {
    if (this.authorizationReference) {
      container.appendChild(this.authorizationReference);
      return;
    }
    this.gapi.analytics.auth.authorize({
      container: container,
      clientid: environment.ga.clientId,
    } as any);
    this.authorizationReference = container;
  }

  public logout() {
    if (this.gapi.analytics.auth) {
      this.gapi.analytics.auth.signOut();
    }
  }

  public get isAuthorized() {
    return this.gapi.analytics.auth.isAuthorized();
  }

  public onSignIn(): Observable<any> {
    const result = new Subject();
    this.gapi.analytics.auth.on('signIn', () => {
      result.next();
    });

    return result;
  }

  public onSignOut(): Observable<any> {
    const result = new Subject();
    this.gapi.analytics.auth.on('signOut', () => {
      result.next();
    });

    return result;
  }

  public createDataSelector(metrics: string, dateFrom: string, dateTo: string, fromUSA: boolean = false) {
    return new this.gapi.analytics.report.Data({
      reportType: 'ga',
      query: {
        metrics,
        'start-date': this.dateTimezoneService.format(dateFrom, 'YYYY-MM-DD'),
        'end-date': this.dateTimezoneService.format(dateTo, 'YYYY-MM-DD'),
        ids: 'ga:' + environment.ga.viewId,
        segment: fromUSA ? environment.ga.usaUsersSegmentId : environment.ga.allUsersSegmentId,
      },
    });
  }

  public createTimeLineSelector(
    container: string | HTMLElement,
    metrics: string,
    segment: string,
    dateFrom: string,
    dateTo: string,
    chartOptions: any = {},
  ) {
    return new this.gapi.analytics.googleCharts.DataChart({
      reportType: 'ga',
      query: {
        dimensions: 'ga:date',
        metrics: metrics,
        'start-date': this.dateTimezoneService.format(dateFrom, 'YYYY-MM-DD'),
        'end-date': this.dateTimezoneService.format(dateTo, 'YYYY-MM-DD'),
        ids: 'ga:' + environment.ga.viewId,
        segment,
      },
      chart: {
        type: 'LINE',
        container: container,
        options: {
          width: '100%',
          ...chartOptions,
        },
      },
    });
  }

  public updateSelectorDates(selector: any, dateFrom: string, dateTo: string) {
    selector
      .set({
        query: {
          'start-date': this.dateTimezoneService.format(dateFrom, 'YYYY-MM-DD'),
          'end-date': this.dateTimezoneService.format(dateTo, 'YYYY-MM-DD'),
        },
      })
      .execute();
  }

  public updateSelectorMetrics(selector: any, metrics: string, segment: string) {
    selector
      .set({
        query: {
          metrics,
          segment,
        },
      })
      .execute();
  }

  public createPieChartSelector(
    container: string | HTMLElement,
    dateFrom: string,
    dateTo: string,
    chartOptions: any = {},
    fromUSA: boolean = false,
  ) {
    return new this.gapi.analytics.googleCharts.DataChart({
      reportType: 'ga',
      query: {
        dimensions: 'ga:userType',
        metrics: 'ga:users',
        'start-date': this.dateTimezoneService.format(dateFrom, 'YYYY-MM-DD'),
        'end-date': this.dateTimezoneService.format(dateTo, 'YYYY-MM-DD'),
        ids: 'ga:' + environment.ga.viewId,
        segment: fromUSA ? environment.ga.usaUsersSegmentId : environment.ga.allUsersSegmentId,
      },
      chart: {
        type: 'PIE',
        container: container,
        options: {
          width: '100%',
          ...chartOptions,
        },
      },
    });
  }

  private checkReady() {
    if (this.gapi) {
      this.isReadyInitialized = true;
      this.gapi.analytics.ready(() => {
        this.onReady$.next(true);
      });
    }
  }
}
